/**
 * billingService.ts — Google Play Billing
 * Todos los métodos de consulta capturan errores y devuelven valores seguros.
 * Solo launchPurchaseFlow relanza el error porque el usuario lo inició intencionalmente.
 */

import { Capacitor, registerPlugin, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/firebase';

// ── IDs de productos según estructura en Play Console ──
export const BILLING_PRODUCT_IDS = {
  PREMIUM_PERSONAL: 'premium_personal',
  PREMIUM_PRO: 'premium_pro',
  // Productos separados PRO por número de perfiles (nuevos)
  PREMIUM_PRO_2: 'premium_pro_2',
  PREMIUM_PRO_3: 'premium_pro_3',
  PREMIUM_PRO_4: 'premium_pro_4',
  PREMIUM_PRO_5: 'premium_pro_5',
  PREMIUM_PRO_6: 'premium_pro_6',
  PREMIUM_PRO_7: 'premium_pro_7',
  PREMIUM_PRO_8: 'premium_pro_8',
  PREMIUM_PRO_9: 'premium_pro_9',
  PREMIUM_PRO_10: 'premium_pro_10',
} as const;

// Mapa de profileCount → productId para los nuevos productos PRO
export const BILLING_PRODUCT_IDS_PRO_BY_PROFILES = {
  2: 'premium_pro_2',
  3: 'premium_pro_3',
  4: 'premium_pro_4',
  5: 'premium_pro_5',
  6: 'premium_pro_6',
  7: 'premium_pro_7',
  8: 'premium_pro_8',
  9: 'premium_pro_9',
  10: 'premium_pro_10',
} as const;

// Helper: obtener productId PRO para un número de perfiles
export function getProProductId(profiles: number): string {
  const productId = BILLING_PRODUCT_IDS_PRO_BY_PROFILES[profiles as keyof typeof BILLING_PRODUCT_IDS_PRO_BY_PROFILES];
  return productId ?? BILLING_PRODUCT_IDS.PREMIUM_PRO; // fallback al legacy si no está en rango
}

export const BILLING_BASE_PLAN_IDS = {
  PREMIUM_PERSONAL: {
    monthly: 'personal-monthly-v2',
    yearly: 'personal-yearly',
  },
  PREMIUM_PRO: {
  2: {
    monthly: 'pro-2-monthly',
    yearly: 'pro-2-yearly',
  },
  3: {
    monthly: 'pro-3-monthly',
    yearly: 'pro-3-yearly',
  },
  4: {
    monthly: 'pro-4-monthly',
    yearly: 'pro-4-yearly',
  },
  5: {
    monthly: 'pro-5-monthly',
    yearly: 'pro-5-yearly',
  },
  6: {
    monthly: 'pro-6-monthly',
    yearly: 'pro-6-yearly',
  },
  7: {
    monthly: 'pro-7-monthly',
    yearly: 'pro-7-yearly',
  },
  8: {
    monthly: 'pro-8-monthly',
    yearly: 'pro-8-yearly',
  },
  9: {
    monthly: 'pro-9-monthly',
    yearly: 'pro-9-yearly',
  },
  10: {
    monthly: 'pro-10-monthly',
    yearly: 'pro-10-yearly',
  },
},
} as const;

export const BILLING_OFFER_ID_PRO_TRIAL      = 'pro-free-trial-1m';
export const BILLING_OFFER_ID_PERSONAL_TRIAL = 'personal-free-trial-1m';

// ── Mensaje de error para UI en español ──
export const BILLING_UNAVAILABLE_MESSAGE =
  'No hemos podido conectar con Google Play. Revisa que Play Store esté actualizado o inténtalo más tarde.';

// ── Tipos ──
export interface BillingProduct {
  productId: string;
  name: string;
  basePlanId: string;
  offerId: string;       // "" si es base plan, "pro-free-trial-1m" si es oferta trial
  offerToken: string;
  offerTags: string;     // comma-separated
  formattedPrice: string;
}

export interface ActivePurchase {
  orderId: string;
  products: string;
  purchaseState: number;
  isAcknowledged: boolean;
  purchaseToken?: string;  // presente en eventos de onPurchasesUpdated
}

export interface PurchasesUpdatedEvent {
  responseCode: number;
  purchases: ActivePurchase[];
}

export interface BillingInitResult {
  connected: boolean;
  responseCode: number;
}

// ── Interfaz del plugin nativo ──
interface BillingPluginInterface {
  initialize(): Promise<BillingInitResult>;
  queryProducts(): Promise<{ products: BillingProduct[] }>;
  queryPurchases(): Promise<{ purchases: ActivePurchase[] }>;
  launchPurchase(opts: {
  productId: string;
  offerToken: string;
  oldPurchaseToken?: string;
  oldProductId?: string;
  replacementMode?: 'upgrade' | 'downgrade';
}): Promise<void>;
  addListener(
    event: 'purchasesUpdated',
    listenerFunc: (data: PurchasesUpdatedEvent) => void
  ): Promise<PluginListenerHandle>;
}

// ── Registro del plugin via patrón estándar de Capacitor ──
const BillingPlugin = registerPlugin<BillingPluginInterface>('BillingPlugin');

// ── Guard de plataforma ──
function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

// ── Inicializar BillingClient ──
// Nunca lanza error al UI: si falla devuelve { connected: false, responseCode: -1 }
export async function initBillingClient(): Promise<BillingInitResult> {
  if (!isNativeAndroid()) {
    console.log('[BILLING] Not native Android — skipping BillingClient init');
    return { connected: false, responseCode: -1 };
  }
  try {
    const result = await BillingPlugin.initialize();
    if (result.connected) {
      console.log('[BILLING] BillingClient initialized OK');
    } else {
      console.warn('[BILLING] BillingClient connected=false, responseCode:', result.responseCode);
    }
    return result;
  } catch (err) {
    console.error('[BILLING] Init error (non-blocking):', err);
    return { connected: false, responseCode: -1 };
  }
}

// ── Consultar productos de suscripción disponibles ──
// Si falla, devuelve [] sin lanzar error al UI
export async function querySubscriptionProducts(): Promise<BillingProduct[]> {
  if (!isNativeAndroid()) return [];
  try {
    const { products } = await BillingPlugin.queryProducts();
    const list = Array.isArray(products) ? products : [];
    console.log('[BILLING] Products fetched:', list.length);
    return list;
  } catch (err) {
    console.warn('[BILLING] queryProducts failed, returning empty list:', err);
    return [];
  }
}

// ── Consultar compras activas del usuario ──
// Si falla, devuelve [] sin lanzar error al UI
export async function queryActivePurchases(): Promise<ActivePurchase[]> {
  if (!isNativeAndroid()) return [];
  try {
    const { purchases } = await BillingPlugin.queryPurchases();
    const list = Array.isArray(purchases) ? purchases : [];
    console.log('[BILLING] Active purchases:', list.length);
    return list;
  } catch (err) {
    console.warn('[BILLING] queryPurchases failed, returning empty list:', err);
    return [];
  }
}

// ── Lanzar flujo de compra ──
// Este SÍ relanza el error: el usuario lo inició intencionalmente y el modal lo captura
export async function launchPurchaseFlow(
  productId: string,
  offerToken: string,
  replacementMode?: 'upgrade' | 'downgrade'
): Promise<void> {
  if (!isNativeAndroid()) {
    throw new Error('[BILLING] launchPurchaseFlow solo disponible en Android nativo');
  }

  try {
    const launchOptions: {
      productId: string;
      offerToken: string;
      oldPurchaseToken?: string;
      oldProductId?: string;
      replacementMode?: 'upgrade' | 'downgrade';
    } = {
      productId,
      offerToken,
    };

    if (replacementMode) {
      const purchases = await queryActivePurchases();

      // Buscar cualquier suscripción PRO activa (legacy o nueva)
      // independientemente del productId específico
      const proProductIds = [
        BILLING_PRODUCT_IDS.PREMIUM_PRO,
        ...Object.values(BILLING_PRODUCT_IDS_PRO_BY_PROFILES),
      ];

      const currentPurchase = purchases.find(
        (purchase) => {
          if (purchase.purchaseState !== 1 || !Boolean(purchase.purchaseToken)) {
            return false;
          }
          const purchaseProducts = purchase.products.split(',');
          return proProductIds.some(proId => purchaseProducts.includes(proId));
        }
      );

      if (!currentPurchase?.purchaseToken) {
        throw new Error(
          '[BILLING] No se encontró la suscripción activa que debe sustituirse'
        );
      }

      launchOptions.oldPurchaseToken =
        currentPurchase.purchaseToken;

      // El oldProductId es el PRO productId actualmente comprado, extraído del currentPurchase
      const purchaseProducts = currentPurchase.products.split(',');
      const oldProductIdFromPurchase = purchaseProducts.find(
        purchaseProductId => proProductIds.includes(purchaseProductId)
      );

      if (!oldProductIdFromPurchase) {
        throw new Error(
          '[BILLING] No se pudo extraer el productId PRO de la suscripción activa'
        );
      }

      launchOptions.oldProductId = oldProductIdFromPurchase;
      launchOptions.replacementMode = replacementMode;
    }

    await BillingPlugin.launchPurchase(launchOptions);

    console.log(
      '[BILLING] launchPurchase resolved — esperando onPurchasesUpdated'
    );
  } catch (err) {
    console.error('[BILLING] launchPurchase error:', err);
    throw err;
  }
}

// ── Registrar listener de compras ──
export async function addPurchaseListener(
  callback: (data: PurchasesUpdatedEvent) => void
): Promise<PluginListenerHandle | null> {
  if (!isNativeAndroid()) return null;
  try {
    return await BillingPlugin.addListener('purchasesUpdated', callback);
  } catch (err) {
    console.error('[BILLING] addPurchaseListener error:', err);
    return null;
  }
}

// ── Registrar listener de vuelta a foreground ──
// Se ejecuta cuando la app vuelve de background (p. ej. tras volver de Google Play checkout).
export async function addResumeListener(
  callback: () => void
): Promise<PluginListenerHandle | null> {
  if (!isNativeAndroid()) return null;
  try {
    return await App.addListener('resume', callback);
  } catch (err) {
    console.error('[BILLING] addResumeListener error:', err);
    return null;
  }
}

// ── Mapear compra activa al plan de suscripción ──
export function mapPurchaseToPlan(purchase: ActivePurchase): string | null {
  if (purchase.purchaseState !== 1) return null; // Solo PURCHASED (1)
  const products = purchase.products.split(',');
  // Reconocer legacy premium_pro
  if (products.includes(BILLING_PRODUCT_IDS.PREMIUM_PRO)) return 'premium_pro';
  // Reconocer nuevos productos separados PRO (premium_pro_2..10)
  const proProductIds = Object.values(BILLING_PRODUCT_IDS_PRO_BY_PROFILES);
  if (products.some(p => proProductIds.includes(p as any))) return 'premium_pro';
  if (products.includes(BILLING_PRODUCT_IDS.PREMIUM_PERSONAL)) return 'premium_personal';
  return null;
}

// ── Verificar compra con backend ──
// Llama verifyGooglePlayPurchase en servidor sin manipular el plan localmente.
// Devuelve result.data si es exitoso, lanza error si falla.
export async function verifyPurchaseWithBackend(purchaseToken: string): Promise<any> {
  try {
    const functions = getFunctions(app);
    const verifyGooglePlayPurchase = httpsCallable(
      functions,
      'verifyGooglePlayPurchase'
    );

    const result = await verifyGooglePlayPurchase({
      purchaseToken,
    });

    console.debug('[BILLING_VERIFY] Server verification successful');
    return result.data;
  } catch (error) {
    console.error('[BILLING_VERIFY] Server verification failed:', error);
    throw error;
  }
}

// ── Verificar puntualmente la compra de un producto concreto ──
// Se usa tras volver del checkout de Google Play para un productId conocido.
// Busca exclusivamente una compra PURCHASED de ese productId exacto.
// No concede nada si no existe una compra válida o ya fue verificada.
// Devuelve el plan identificado si la verificación contra backend fue exitosa,
// null si aún no existe compra válida o la verificación falló.
export async function verifyPendingPurchase(
  expectedProductId: string
): Promise<string | null> {
  if (!expectedProductId) return null;

  const purchases = await queryActivePurchases();

  const match = purchases.find((p) => {
    if (p.purchaseState !== 1 || !Boolean(p.purchaseToken)) {
      return false;
    }
    const purchaseProducts = p.products.split(',');
    return purchaseProducts.includes(expectedProductId);
  });

  if (!match?.purchaseToken) {
    return null;
  }

  const plan = mapPurchaseToPlan(match);
  if (!plan) {
    return null;
  }

  try {
    await verifyPurchaseWithBackend(match.purchaseToken);
    console.log('[BILLING] verifyPendingPurchase successful for', expectedProductId);
    return plan;
  } catch (error) {
    console.warn(
      '[BILLING] verifyPendingPurchase failed for',
      expectedProductId,
      ':',
      error
    );
    return null;
  }
}

// ── Restaurar compras: acción manual de recuperación ──
// Verifica contra backend usando verifyPurchaseWithBackend().
// El plan funcional se actualizará desde workspaceMetadata server-side.
export async function restorePurchases(): Promise<string> {
  const purchases = await queryActivePurchases();

  for (const p of purchases) {
    const plan = mapPurchaseToPlan(p);

    if (plan && p.purchaseToken) {
      try {
        await verifyPurchaseWithBackend(p.purchaseToken);
        return plan;
      } catch (error) {
        console.warn(
          '[RESTORE_PURCHASES] Verification failed:',
          error
        );
        return 'free';
      }
    }
  }

  return 'free';
}
